import { create } from 'zustand'
import { persist } from 'zustand/middleware'

/**
 * Remembers that the visitor closed the "install this app" invitation.
 *
 * The invitation is shown at most until it is dismissed once — closing it is
 * a definitive answer, not a "later", so the timestamp is kept forever rather
 * than expiring like the cookie consent does. Re-offering an install to
 * someone who already said no is exactly the nag this avoids.
 *
 * Accepting the install counts as a dismissal too: the browser fires
 * `appinstalled` and there is nothing left to invite.
 */
interface InstallPromptState {
  dismissedAt: string | null
  dismiss: () => void
  /** Test/debug escape hatch — also used by nothing in the UI on purpose. */
  reset: () => void
}

export const useInstallPromptStore = create<InstallPromptState>()(
  persist(
    (setState) => ({
      dismissedAt: null,

      dismiss: () => setState({ dismissedAt: new Date().toISOString() }),
      reset: () => setState({ dismissedAt: null }),
    }),
    {
      name: 'pwa-install-prompt',
      partialize: (state) => ({ dismissedAt: state.dismissedAt }),
    },
  ),
)

/** True once the invitation has been closed (or the app installed). */
export const selectHasDismissedInstallPrompt = (state: InstallPromptState) =>
  state.dismissedAt !== null
